import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { processFileForUpload } from '../../utils/compressImage';
import DepartmentSelect from '../Departments/DepartmentSelect';
import { addMonthsToDay, formatDay, validateUploadForm, renewalSummary, uploadModeFor, keepsDates } from './uploadContractForm';

const fontBase = { fontFamily: 'Roboto, Arial, sans-serif' };
const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx';
const today = () => new Date().toISOString().split('T')[0];

const sizeText = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const initialValues = (contract) => {
  const r = contract?.renewalOptions || {};
  // A correction or re-issue starts with the dates blank, which keeps the ones
  // it has — renewals included.
  const keeping = keepsDates(uploadModeFor(contract));
  return {
    file: null,
    startDate: keeping ? '' : today(),
    validity: !keeping && contract?.validityMonths ? String(contract.validityMonths) : '',
    noticePeriod: contract?.noticePeriodMonths ? String(contract.noticePeriodMonths) : '',
    department: contract?.department || '',
    comment: '',
    // A replacement starts from what the contract already has, so uploading a
    // new copy never silently switches auto-renew off.
    autoRenew: Boolean(r.autoRenew),
    renewalTerm: r.renewalTermMonths ? String(r.renewalTermMonths) : '',
    renewalNotice: String(r.renewalNoticePeriod ?? 30),
  };
};

/**
 * Upload the signed contract, and decide whether it renews automatically.
 *
 * Laid out like the app's other form dialogs (RegistrationDecisionDialog,
 * ActionModal): labelled fields with helper text, errors once a field has been
 * touched, Cancel and a green primary action on the right. The rules live in
 * uploadContractForm.js; the server enforces the same ones.
 */
const UploadContractModal = ({ open, onClose, onSave, uploading, contract }) => {
  const theme = useTheme();
  const green = theme.palette.green || { main: '#578A18', hover: '#467014' };
  const mode = uploadModeFor(contract);
  const keeping = keepsDates(mode);
  const fileInput = useRef(null);

  const [values, setValues] = useState(() => initialValues(contract));
  const [touched, setTouched] = useState({});
  const [termEdited, setTermEdited] = useState(false);

  // A form left half-filled for another contract must not carry over.
  useEffect(() => {
    if (open) {
      setValues(initialValues(contract));
      setTouched({});
      setTermEdited(Boolean(contract?.renewalOptions?.renewalTermMonths));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (value) => setValues((v) => {
    const next = { ...v, [key]: value };
    // Until legal types a renewal term of their own, it follows the validity.
    if (key === 'validity' && !termEdited) next.renewalTerm = value;
    if (key === 'autoRenew' && value && !next.renewalTerm) next.renewalTerm = next.validity;
    return next;
  });
  const touch = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const errors = validateUploadForm(values, { keepsDates: keeping });
  const showError = (key) => (touched[key] ? errors[key] : undefined);
  const expiry = addMonthsToDay(values.startDate, values.validity);
  const summary = renewalSummary(values);

  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    touch('file')();
    if (!picked) return;
    try {
      set('file')(await processFileForUpload(picked));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSave = () => {
    setTouched({ file: true, startDate: true, validity: true, noticePeriod: true, renewalTerm: true, renewalNotice: true });
    if (Object.keys(errors).length) return;
    onSave(values);
  };

  const field = { ...fontBase, '& .MuiInputBase-input': { fontSize: '14px' } };
  const months = <InputAdornment position="end"><Typography sx={{ ...fontBase, fontSize: '13px', color: '#6b7280' }}>months</Typography></InputAdornment>;

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '8px' } }}>
      <DialogTitle sx={{ ...fontBase, fontWeight: 600, fontSize: '18px', color: '#111827', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {{
          correcting: 'Upload the corrected contract',
          reissue: 'Upload a new copy of the contract',
          reactivate: 'Renew the expired contract',
        }[mode] || 'Upload signed contract'}
        <IconButton onClick={onClose} disabled={uploading} size="small" sx={{ color: '#6b7280' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ ...fontBase, color: '#4b5563', fontSize: '14px', mb: 2.5 }}>
          {{
            correcting: 'Replace the document, and any dates that were wrong. The supplier is sent the corrected copy when you resubmit.',
            reissue: 'The new copy replaces the current one and the supplier is sent it. The contract keeps its dates unless you set new ones.',
            reactivate: 'Upload the signed renewal and set its new dates. The contract becomes active again and the supplier is sent a copy.',
          }[mode] || 'The contract goes live when it is uploaded, and the supplier is sent a copy.'}
        </Typography>

        {/* Signed contract */}
        <Box sx={{ mb: 2.5 }}>
          <input ref={fileInput} type="file" accept={ACCEPT} hidden onChange={handleFile} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInput.current?.click()}
              sx={{ ...fontBase, textTransform: 'none', borderColor: showError('file') ? '#b91c1c' : '#d1d5db', color: '#374151' }}
            >
              {values.file ? 'Choose a different file' : 'Choose file'}
            </Button>
            <Typography sx={{ ...fontBase, fontSize: '14px', color: values.file ? '#111827' : '#6b7280' }}>
              {values.file ? `${values.file.name} · ${sizeText(values.file.size)}` : 'No file chosen'}
            </Typography>
          </Box>
          <Typography sx={{ ...fontBase, fontSize: '12px', mt: 0.75, color: showError('file') ? '#b91c1c' : '#6b7280' }}>
            {showError('file') || 'PDF preferred. Word, Excel and images are accepted, up to 20 MB.'}
          </Typography>
        </Box>

        {/* Dates */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            label="Start date"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={values.startDate}
            onChange={(e) => set('startDate')(e.target.value)}
            onBlur={touch('startDate')}
            error={Boolean(showError('startDate'))}
            helperText={showError('startDate') || (keeping ? 'Leave blank to keep the current dates' : ' ')}
            sx={field}
          />
          <TextField
            label="Validity"
            type="number"
            size="small"
            fullWidth
            required={!keeping}
            inputProps={{ min: 1, max: 120, step: 1 }}
            InputProps={{ endAdornment: months }}
            value={values.validity}
            onChange={(e) => set('validity')(e.target.value)}
            onBlur={touch('validity')}
            error={Boolean(showError('validity'))}
            helperText={showError('validity') || (expiry ? `Expires on ${formatDay(expiry)}` : (keeping ? 'Leave blank to keep the current dates' : 'How long the contract runs'))}
            sx={field}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            label="Notice period (optional)"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0, max: 60, step: 1 }}
            InputProps={{ endAdornment: months }}
            value={values.noticePeriod}
            onChange={(e) => set('noticePeriod')(e.target.value)}
            onBlur={touch('noticePeriod')}
            error={Boolean(showError('noticePeriod'))}
            helperText={showError('noticePeriod') || 'The notice either party must give to end it'}
            sx={field}
          />
          <Box sx={{ flex: 1, width: '100%' }}>
            <DepartmentSelect
              value={values.department}
              onChange={set('department')}
              label="Department"
              helperText="Where the contract is executed"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Auto-renewal — legal's decision, the same setting as on the contract page */}
        <FormControlLabel
          control={<Switch checked={values.autoRenew} onChange={(e) => set('autoRenew')(e.target.checked)} />}
          label={<Typography sx={{ ...fontBase, fontSize: '14px', fontWeight: 500, color: '#111827' }}>Renew automatically at the end date</Typography>}
        />
        {values.autoRenew && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Each renewal lasts"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 120, step: 1 }}
              InputProps={{ endAdornment: months }}
              value={values.renewalTerm}
              onChange={(e) => { setTermEdited(true); set('renewalTerm')(e.target.value); }}
              onBlur={touch('renewalTerm')}
              error={Boolean(showError('renewalTerm'))}
              helperText={showError('renewalTerm') || 'Defaults to the validity'}
              sx={field}
            />
            <TextField
              label="Warn legal and the department head"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, max: 365, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ ...fontBase, fontSize: '13px', color: '#6b7280' }}>days before</Typography></InputAdornment> }}
              value={values.renewalNotice}
              onChange={(e) => set('renewalNotice')(e.target.value)}
              onBlur={touch('renewalNotice')}
              error={Boolean(showError('renewalNotice'))}
              helperText={showError('renewalNotice') || 'So it can be stopped in time'}
              sx={field}
            />
          </Box>
        )}
        {summary && (
          <Typography sx={{ ...fontBase, fontSize: '13px', color: '#374151', mt: 1.5, p: 1.5, bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
            {summary}
          </Typography>
        )}

        <TextField
          label="Notes (optional)"
          fullWidth
          multiline
          minRows={3}
          value={values.comment}
          onChange={(e) => set('comment')(e.target.value)}
          placeholder="Internal notes for legal, procurement and the HOD (not shown to the supplier)"
          sx={{ ...field, mt: 2.5 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={uploading} sx={{ ...fontBase, textTransform: 'none', color: '#374151' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={uploading}
          sx={{ ...fontBase, textTransform: 'none', bgcolor: green.main, '&:hover': { bgcolor: green.hover } }}
        >
          {uploading ? 'Uploading…' : mode === 'correcting' ? 'Upload correction' : mode === 'reissue' ? 'Upload new copy' : 'Upload contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadContractModal;
